<template>
  <div class="project-summary">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        sticky
    >
      <template #actions>
        <b-button @click="backClicked" icon-left="arrow-left" tabindex="1">
          {{ $t('common.back') }}
        </b-button>
        <OmxPermissionButton
            :allowed="currentUserAccess('ProjectLogs', 'VIEW', project_id)"
            icon-left="eye"
            tag="router-link"
            :to="{ name: 'ProjectLogs', params: { project_id } }"
        >{{ $t('projects-view.enter-worklog') }}</OmxPermissionButton>
      </template>
    </OmxPageHeader>

    <OmxCard density="compact" class="project-summary__tabs-card">
      <b-tabs type="is-toggle" v-model="activeTab" class="project-summary__tabs">
        <b-tab-item :label="$t('project-summary-overview.overview-title')" icon="view-dashboard">
          <div class="project-summary__overview">
            <ProjectSummaryOverview :project_id="project_id" />
          </div>
        </b-tab-item>
        <b-tab-item :label="$t('project-summary-view.per-day-title')" icon="calendar">
          <div class="project-summary__per-day">
            <div class="project-summary__pane project-summary__pane--list">
              <GroupedLogs :project_id="project_id" @groupLogSelected="onGroupLogSelected" />
            </div>
            <div class="project-summary__pane project-summary__pane--detail">
              <GroupedLogDetails :selected_grouped_log="selectedGroupedLog" />
            </div>
          </div>
        </b-tab-item>
        <b-tab-item :label="$t('project-summary-view.per-task-title')" icon="format-list-checks">
          <div class="project-summary__per-task">
            <GroupedLogsPerTask :project_id="project_id" />
          </div>
        </b-tab-item>
      </b-tabs>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import GroupedLogs from "@/components/GroupedLogs";
import GroupedLogDetails from "@/components/GroupedLogDetails";
import GroupedLogsPerTask from "@/components/GroupedLogsPerTask";
import ProjectSummaryOverview from "@/components/ProjectSummaryOverview.vue";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";

export default {
  name: "ProjectSummary",
  components: {
    OmxPageHeader,
    OmxCard,
    GroupedLogs,
    GroupedLogDetails,
    GroupedLogsPerTask,
    ProjectSummaryOverview
  },
  props: {
    project_id: { type: String }
  },
  data() {
    return {
      isLoading: false,
      projectCode: "",
      projectTitle: "",
      selectedGroupedLog: { grouped_log: {} },
      activeTab: 0
    };
  },
  computed: {
    // Mirror the Project Details header: project name as title, code as subtitle,
    // and no status/phase pill.
    headerTitle() {
      return this.getAdjustedTitle(this.projectTitle);
    },
    headerSubtitle() {
      return this.projectCode || "";
    }
  },
  methods: {
    readProjectData() {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectFunction({ action: "get_project_by_id", document_id: this.project_id, fill_meta: true })
          .then((result) => {
            this.isLoading = false;
            this.projectCode = result.data.projects.code;
            this.projectTitle = result.data.projects.title;
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message);
            if (error.message === "InvalidPath") {
              this.$router.replace("../projects");
            }
          });
    },
    onGroupLogSelected(groupLog) {
      this.selectedGroupedLog = groupLog;
    },
    backClicked() {
      this.$router.back();
    }
  },
  mounted() {
    if (!this.currentUserAccess("ProjectSummary", 'VIEW', this.project_id)) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => { /* navigation duplicated */ });
    }
    this.readProjectData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.project-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.project-summary__tabs-card {
  padding: 0 !important;
  overflow: hidden;
  ::v-deep .b-tabs .tabs ul {
    border-bottom: 1px solid $omx-ink-100;
  }
  ::v-deep .b-tabs .tab-content { padding: 18px; }
}

.project-summary__per-day {
  display: grid;
  grid-template-columns: minmax(280px, 5fr) minmax(420px, 7fr);
  gap: 14px;
  min-height: 540px;
  max-height: 80vh;
}

.project-summary__pane {
  background: $omx-surface;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-md;
  overflow-y: auto;
  min-height: 0;
  max-height: 80vh; // the scroll bound must live on the scrolling element itself
}
.project-summary__pane--list   { padding: 4px 4px 12px; }
.project-summary__pane--detail { padding: 12px; }

.project-summary__per-task {
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-md;
  padding: 4px;
}

@media (max-width: 900px) {
  .project-summary__per-day {
    grid-template-columns: 1fr;
    max-height: none;
  }
  .project-summary__pane { max-height: none; }
}
</style>
